import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { UniqueEntityID } from '@/domain/core'
import { MomentIndex, VariableArm } from '@/domain/models'
import {
  WBProfileAirplaneTemplate,
  WBProfileAirplaneTemplateProps,
  WBTemplateStations
} from '@/domain/models/WBProfileAirplaneTemplate/WBProfileAirplaneTemplate'
import { Result } from '@/domain/protocols/Result'
import { FuelQuantityUnit, WeightUnit } from '@/domain/protocols/Unit'

import {
  IWBProfileAirplaneTemplateRepository,
  WBProfileAirplaneTemplateDTO,
  WBStationsDTOTemplate
} from './IWBProfileAirplaneTemplateRepository'

import type { IHttpClient } from '@/infra/http/IHttpClient'
import { HTTP_TIMEOUTS } from '@/infra/http/timeouts'
import { normalizeUnit } from '@/utils/unit'
import { hasCompleteEnvelopeSequence } from '@/utils/wbEnvelopeOrder'

/**
 * O catálogo é editado fora do app e já publicou unidade com grafia divergente ("us gal/h" com
 * espaço, no catálogo de modelos). Canonizamos caixa, espaço e underscore, e **mantemos o rótulo cru
 * quando não reconhecemos**: os valores do template estão na unidade que ele mesmo declara, então
 * recuar para a unidade de domínio reinterpretaria cada número em silêncio.
 */
const _normalizeCapacityUnit = <T extends WeightUnit | FuelQuantityUnit>(rawUnit: T): T =>
  (normalizeUnit.fuelQuantity(rawUnit) as T) ?? rawUnit

/**
 * O catálogo publica a tabela de braço variável com o momento **dividido pelo `momentIndex`** do
 * modelo, que é como a POH a imprime. No resto do app o momento é cru — peso × braço nas unidades do
 * perfil —, então a des-indexação acontece aqui, uma vez, na borda de leitura: depois dela um
 * consumidor de `variableArms` não precisa saber de onde a tabela veio.
 *
 * O índice vem cru do arquivo, sem esquema que o garanta. Sem índice utilizável o momento passa como
 * está, porque `NaN` num momento não desanda um número só: ele contamina o braço interpolado do
 * tanque inteiro.
 */
const _rawMoments = (variableArms: VariableArm[] | undefined, momentIndex: MomentIndex): VariableArm[] => {
  if (!variableArms) return variableArms
  if (!Number.isFinite(momentIndex) || momentIndex <= 0) return variableArms

  return variableArms.map((variableArm) => ({ ...variableArm, moment: variableArm.moment * momentIndex }))
}

@injectable()
export class WBProfileAirplaneTemplateRepository implements IWBProfileAirplaneTemplateRepository {
  private templates: WBProfileAirplaneTemplate[] = []
  // Dedupes concurrent callers onto a single request while the templates are being fetched.
  private loadPromise: Promise<Result<WBProfileAirplaneTemplate[]>> | null = null

  constructor(@inject(InjectionTokens.HttpClient) private httpClient: IHttpClient) {}

  /**
   * Pela CDN, e não pela origem do bucket, pelo mesmo motivo do catálogo de modelos: a borda comprime
   * e a origem não (146 655 bytes contra 14 080, medido em 03/09).
   */
  protected getRemoteUrl(): string {
    return `${import.meta.env.VITE_JETSTREAM_CDN_BASEURL}/global/wb_profile_aircraft_models.json`
  }

  async getWBProfileAirplaneTemplates(): Promise<Result<WBProfileAirplaneTemplate[]>> {
    if (this.templates.length > 0) {
      return Result.ok(this.templates)
    }

    if (this.loadPromise) return this.loadPromise

    this.loadPromise = this._fetchTemplates()
    try {
      return await this.loadPromise
    } finally {
      // Clear on both success and failure so a failed fetch doesn't pin an empty result — the next
      // caller retries from scratch.
      this.loadPromise = null
    }
  }

  private async _fetchTemplates(): Promise<Result<WBProfileAirplaneTemplate[]>> {
    try {
      // O retry de flap de rede vive no AxiosHttpClient (transporte). Antes era refeito aqui, com
      // re-throw manual do erro transiente, porque o client engolia a rejeicao do axios num
      // `{ success: false }` — agora o proprio client retenta e preserva `code`/`isTransient`.
      const response = await this.httpClient.get<WBProfileAirplaneTemplateDTO[]>(this.getRemoteUrl(), {
        timeout: HTTP_TIMEOUTS.dataset
      })

      if (response.success === false || response.status === 500) {
        return Result.fail('Internal server error')
      } else if (response.status < 200 || response.status >= 300) {
        return Result.fail('Failed to get aircraft profile templates')
      }

      const templates = response.response.map((dto) => {
        return this._remoteToLocal(dto)
      })

      this.templates = templates
      return Result.ok(this.templates)
    } catch (error) {
      // O retry de transporte do AxiosHttpClient esgotou as tentativas num flap persistente.
      return Result.fail('Failed to get aircraft profile templates')
    }
  }

  async getWBProfileAirplaneTemplateByAircraftModelIds(
    aircraftModelId: UniqueEntityID[]
  ): Promise<Result<WBProfileAirplaneTemplate[]>> {
    const allTemplatesResult = await this.getWBProfileAirplaneTemplates()

    if (allTemplatesResult.isFailure) {
      return Result.fail(allTemplatesResult.error)
    }

    const allTemplates = allTemplatesResult.getValue()
    const filteredTemplates = allTemplates.filter((template) =>
      aircraftModelId.some((id) => id.toString() === template.aircraftModelId)
    )

    return Result.ok(filteredTemplates)
  }

  private _remoteToLocal(dto: WBProfileAirplaneTemplateDTO): WBProfileAirplaneTemplate {
    const publishesSequence = hasCompleteEnvelopeSequence(dto.envelopes)

    const props: WBProfileAirplaneTemplateProps = {
      aircraftModelId: dto.aircraftModelId,
      name: dto.name,
      namePT: dto.namePT,
      reference: dto.reference,
      weightUnit: normalizeUnit.weight(dto.weightUnit) ?? dto.weightUnit,
      armUnit: normalizeUnit.linearMeasure(dto.armUnit) ?? dto.armUnit,
      fuelQuantityUnit: normalizeUnit.fuelQuantity(dto.fuelQuantityUnit) ?? dto.fuelQuantityUnit,
      momentIndex: dto.momentIndex,
      lemac: dto.lemac,
      macLength: dto.macLength,
      basicEmptyWeight: dto.basicEmptyWeight,
      basicEmptyWeightArm: dto.basicEmptyWeightArm,
      maximumTakeoffWeight: dto.maximumTakeoffWeight,
      maximumZeroFuelWeight: dto.maximumZeroFuelWeight,
      maximumRampWeight: dto.maximumRampWeight,
      maximumLandingWeight: dto.maximumLandingWeight,
      stations: dto.stations.map((station) => {
        return {
          id: new UniqueEntityID(station.id),
          name: station.name,
          namePT: station.namePT,
          type: station.type,
          armType: station.armType,
          fixedArm: station.fixedArm,
          maxCapacity: station.maxCapacity,
          capacityUnit: _normalizeCapacityUnit(station.capacityUnit),
          includeInBOW: station.includeInBOW,
          predefinedWeight: station.predefinedWeight,
          variableArms: _rawMoments(station.variableArms, dto.momentIndex),
          priority: station.priority
        } as WBTemplateStations
      }),
      // O catálogo publica os pontos na ordem do contorno lido do manual — é o ciclo correto em 53
      // de 53 modelos, os três côncavos incluídos. Quando o campo `pointSequence` vier no arquivo,
      // ele manda; até vir, a posição no array é a sequência, e o perfil criado a partir do
      // template já nasce com o contorno gravado.
      //
      // A escolha é do envelope inteiro, não de cada ponto: preencher só o que faltou misturaria a
      // sequência publicada com a posição no array, que são contornos diferentes se o arquivo não
      // vier ordenado pela sequência.
      envelope: dto.envelopes.map((envelope, index) => {
        return {
          id: new UniqueEntityID(envelope.id),
          weight: envelope.weight,
          arm: envelope.arm,
          macPercent: envelope.macPercent,
          pointSequence: publishesSequence ? envelope.pointSequence : index + 1
        }
      })
    }

    return WBProfileAirplaneTemplate.create(props, new UniqueEntityID(dto.id))
  }
}
